/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  APIConsumerChargeCommercialResponseInterface,
  ConsumerChargeCommercialInitialProperties,
  RequestConsumerChargeCommercialInterface,
} from '../../interface/end-consumer-charge-Session-commercial-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import emspSessionApi from '../../utils/api/v2/emsp-session';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchConsumerChargeCommercial = createAsyncThunk<
  APIConsumerChargeCommercialResponseInterface,
  RequestConsumerChargeCommercialInterface
>(
  'end-consumers/fetchConsumerChargeCommercial',
  async (data: RequestConsumerChargeCommercialInterface, { dispatch }) => {
    const { userCustomId, searchBy, type, ...query } = data;
    if (!userCustomId) {
      throw new Error('userCustomId is required');
    }
    if (searchBy) {
      query.search = searchBy;
    }
    const response = await emspSessionApi
      .fetchCommercialSessionList({ userCustomId, ...removeEmptyKeys(query) })
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return {
      ...response.data,
      results: response.data.results.map((consumerData: any) => ({
        ...consumerData,
        createdAt: consumerData.sessionStartAt || consumerData.createdAt,
        paymentMethod:
          consumerData?.paymentMethod === 'hyperpay' ? 'Credit Card' : consumerData?.paymentMethod,
        chargingPoint: {
          chargingPointSn: consumerData?.evseUid || consumerData?.chargingPointSn || consumerData?.chargingPoint?.chargingPointSn,
          chargingPointName: consumerData?.evseUid || consumerData?.chargingPointName || consumerData?.chargingPoint?.chargingPointName,
          _id: consumerData?.chargingPointId || consumerData?.chargingPoint?._id,
        },
        chargingStation: {
          name: consumerData?.locationId || consumerData?.chargingStationName || consumerData?.chargingStation?.name,
          _id: consumerData?.locationId || consumerData?.chargingStationId || consumerData?.chargingStation?._id,
        },
        vehicle: {
          manufacturer: consumerData?.driver?.vehicle?.manufacturer || consumerData?.vehicleName || consumerData?.vehicle?.manufacturer,
          model: consumerData?.driver?.vehicle?.model || consumerData?.vehicleModel || consumerData?.vehicle?.model,
          connectorType: consumerData?.driver?.vehicle?.connectorType || consumerData?.vehicle?.connectorType,
        },
        powerConsumedInKwh: consumerData?.energyKwh || consumerData?.powerConsumedInKwh,
        walletHistoryObject: {
          paymentStatus: consumerData?.paymentStatus || consumerData?.walletHistoryObject?.paymentStatus,
          amount: consumerData?.totalCost || consumerData?.amount || consumerData?.walletHistoryObject?.amount,
        },
        hyperpayTransaction: {
          hyperpayTransactionId: consumerData?.transactionId || consumerData?.hyperpayTransactionId || consumerData?.hyperpayTransaction?.hyperpayTransactionId,
          status: consumerData?.hyperpayTransactionIdStatus || consumerData?.hyperpayTransaction?.status,
        },
      })),
    };
  },
);

export const fetchConsumerChargeCommercialFilter = createAsyncThunk<CommonFilterProperties[], void>(
  'end-consumers/fetchConsumerChargeCommercialFilter',
  async (_, { dispatch, getState }) => {
    const state: any = getState();
    const { selectedEndConsumer } = state.endConsumerType;

    if (!selectedEndConsumer?.userCustomId) {
      throw new Error('accountCustomId is required');
    }
    try {
      const response = await emspSessionApi.fetchCommercialListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ConsumerChargeCommercialInitialProperties = {
  consumers: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const ConsumerChargeCommercial = createSlice({
  name: 'end-consumers-charge-commercial',
  initialState,
  reducers: {
    /**
     * Update By month filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateConsumerChargeCommercialListFilter: (
      state,
      action: PayloadAction<FilterUpdateProperties>,
    ) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder: ActionReducerMapBuilder<ConsumerChargeCommercialInitialProperties>) => {
    builder
      .addCase(
        fetchConsumerChargeCommercial.pending,
        (state: ConsumerChargeCommercialInitialProperties) => {
          state.loading = true;
        },
      )
      .addCase(
        fetchConsumerChargeCommercial.fulfilled,
        (
          state: ConsumerChargeCommercialInitialProperties,
          action: PayloadAction<APIConsumerChargeCommercialResponseInterface>,
        ) => {
          state.loading = false;
          state.consumers = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(
        fetchConsumerChargeCommercial.rejected,
        (state: ConsumerChargeCommercialInitialProperties) => {
          state.loading = false;
          // Handle error if needed
        },
      );

    builder
      .addCase(fetchConsumerChargeCommercialFilter.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchConsumerChargeCommercialFilter.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchConsumerChargeCommercialFilter.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const { updateConsumerChargeCommercialListFilter, setSortData } =
  ConsumerChargeCommercial.actions;
// Export the reducer
export default ConsumerChargeCommercial.reducer;
